import { useTranslation } from 'react-i18next'
import {
  Card,
  FilterContainer,
  PageHeader,
  Heading,
  GroupedUsersMultiSelect,
  MultiSelect,
  DownloadFile,
  Button,
  EmptyState,
  NoDataIcon,
} from '@lmc/ui-kit'
import styled from 'styled-components'
import {
  UtilisationAndAttendanceIcon,
  TravelAndGapIcon,
  OperationalIssuesIcon,
} from 'screens/Rostering/RotaBuilder/common/components/icons'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Text } from '@lmc/ui-kit/src/general'
import { useState } from 'react'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { useSelector } from 'react-redux'
import { HomeDto } from '@lmc/ts-common'
import {
  useRotaOverview,
  RotaOverviewFilters,
} from 'screens/Rostering/hooks/useRotaOverview'
import { Table } from 'antd'
import WeekRangePicker from './components/WeekRangePicker'
import {
  TravelRateCardLevel,
  TravelRateCardStatus,
  useGetTravelRateCardSettings,
} from 'screens/Rostering/hooks/useTravelRateCardSettings'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  getOverviewColumns,
  formatMinutesToHours,
  formatMetersToMiles,
} from './getOverviewColumns'
import { useExportRotaOverviewCsv } from './useExportRotaOverview'
import { Spinner } from 'components/generic'

const RotaOverview = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<RotaOverviewFilters>({
    to: new Date(),
    from: new Date(),
    teamMembers: [],
    contractTypes: [],
  })

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )

  const flags = useFeatureFlags({
    flagNames: ['travel_and_mileage'],
  })?.data

  const flagsTravelTimeAndMileageEnabled = flags?.travel_and_mileage?.enabled

  const { data: rateCards } = useGetTravelRateCardSettings()
  const siteRateCard = rateCards?.find(
    (card) => card.levelType === TravelRateCardLevel.SITE,
  )

  const contractTypeOptions =
    home.rotaSettings?.contractTypes?.map((contractType) => ({
      label: contractType.name,
      value: contractType.id,
    })) || []

  const { users } = useUsersList({})

  const { overview, isLoading: isOverviewLoading } = useRotaOverview(filters)

  const exportCsv = useExportRotaOverviewCsv(filters)

  const handleFilterChange = (newValue: Partial<RotaOverviewFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newValue,
    }))
  }

  const columns = getOverviewColumns(
    t,
    flagsTravelTimeAndMileageEnabled,
    siteRateCard,
  )

  return (
    <MainContainer>
      <PageHeader title={t('ROTA_OVERVIEW_REPORT')} />
      <FilterContainer title={t('FILTERS')}>
        <Filters>
          <LeftContainer>
            <WeekRangePicker
              value={[filters.from, filters.to]}
              onChange={(dates) =>
                handleFilterChange({
                  from: dates[0],
                  to: dates[1],
                })
              }
            />
            <GroupedUsersMultiSelect
              users={users}
              title={t('TEAM_MEMBERS')}
              values={filters.teamMembers}
              handleInputChange={(selectedOptions) =>
                handleFilterChange({
                  teamMembers: selectedOptions as string[],
                })
              }
            />
            <StyledMultiSelect
              title={t('CONTRACT_TYPES')}
              options={contractTypeOptions}
              values={filters.contractTypes}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedTypes) =>
                handleFilterChange({
                  contractTypes: selectedTypes as string[],
                })
              }
            />
          </LeftContainer>
          <RightAlign>
            <Button onClick={exportCsv} icon={<DownloadFile />}>
              {t('DOWNLOAD_CSV')}
            </Button>
          </RightAlign>
        </Filters>
      </FilterContainer>
      <ContentContainer>
        {siteRateCard?.status === TravelRateCardStatus.ENABLED ? (
          <>
            <StyledCard>
              <CardHeader>
                <UtilisationAndAttendanceIcon />
                <CardHeading level={4}>
                  {t('UTILISATION_AND_ATTENDANCE')}
                </CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_WORKED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.hoursWorked ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('ABSENT_DAYS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.absentDays || 0}{' '}
                    {t('DAYS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNDERUTILISED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .underutilisedMinutes || 0,
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('OVERTIME_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .overtimeMinutes || 0,
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </StyledCard>
            <FlexContainer>
              <FlexCard>
                <CardHeader>
                  <TravelAndGapIcon />
                  <CardHeading level={4}>{t('TRAVEL_AND_GAP')}</CardHeading>
                </CardHeader>
                <Divider />
                <DetailsContainer>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('TRAVEL_TIME')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMinutesToHours(
                        overview?.summary.travelAndGap.travelMinutes || 0,
                      )}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('MILEAGE')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMetersToMiles(
                        overview?.summary.travelAndGap.distanceMeters || 0,
                      )}{' '}
                      {t('MILES')}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('TOTAL_VISIT_GAPS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {overview?.summary.travelAndGap.totalVisitGaps || 0}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                </DetailsContainer>
              </FlexCard>
              <FlexCard>
                <CardHeader>
                  <OperationalIssuesIcon />
                  <CardHeading level={4}>{t('OPERATIONAL_ISSUES')}</CardHeading>
                </CardHeader>
                <Divider />
                <DetailsContainer>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('EXCEPTIONS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {overview?.summary.operationalIssues.exceptions || 0}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('UNALLOCATED_HOURS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMinutesToHours(
                        overview?.summary.operationalIssues
                          .unallocatedMinutes || 0,
                      )}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                </DetailsContainer>
              </FlexCard>
            </FlexContainer>
          </>
        ) : (
          <FlexContainer>
            <FlexCard>
              <CardHeader>
                <UtilisationAndAttendanceIcon />
                <CardHeading level={4}>
                  {t('UTILISATION_AND_ATTENDANCE')}
                </CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_WORKED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.hoursWorked ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('ABSENT_DAYS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.absentDays || 0}{' '}
                    {t('DAYS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNDERUTILISED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .underutilisedMinutes || 0,
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('OVERTIME_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .overtimeMinutes || 0,
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </FlexCard>
            <FlexCard>
              <CardHeader>
                <OperationalIssuesIcon />
                <CardHeading level={4}>{t('OPERATIONAL_ISSUES')}</CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('EXCEPTIONS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.operationalIssues.exceptions || 0}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNALLOCATED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.operationalIssues.unallocatedMinutes ||
                        0,
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </FlexCard>
          </FlexContainer>
        )}
        {isOverviewLoading ? (
          <Spinner />
        ) : overview?.teamMemberStats?.length ? (
          <StyledTable
            columns={columns}
            dataSource={overview.teamMemberStats}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => t('TOTAL_ITEMS', { total }),
            }}
            scroll={{ x: 'max-content' }}
            loading={false}
            tableLayout="fixed"
          />
        ) : (
          <EmptyState title={t('NO_DATA_TO_SHOW')} icon={<NoDataIcon />} />
        )}
      </ContentContainer>
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-x: auto;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FlexContainer = styled.div`
  display: flex;
  gap: 16px;
`

const StyledCard = styled(Card)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FlexCard = styled(StyledCard)`
  flex: 1;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const DetailsContainer = styled.div`
  display: flex;
`

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const DetailSectionHeading = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DetailSectionValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightAlign = styled.div`
  display: flex;
  align-items: center;
`

const StyledTable = styled(Table)`
  .ant-table {
    width: 100%;
    min-width: 1000px;
  }

  .ant-table-column-sorters {
    gap: 8px;
    flex-direction: row-reverse;
  }

  .ant-table-cell {
    padding: 16px 12px !important;
  }
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 200px;
`

export default RotaOverview
